/**
 * The Customize wiring (1.Y deliverable 10, over 1.AC-2's dialog). `CustomizeProvider` mounts
 * 1.AC-2's `CustomizeDialogProvider` and gives `useCustomizeHandlers()`:
 *
 * - `fromReference(target)`: "Customize" on a reference page (`ReferenceActionsProvider`'s
 *   `onCustomize`): the dialog for that creature, no repoint offered; a created copy opens its form.
 * - `fromEmbed(embed, editor)`: "Customize" on an SRD embed (`partOptions.embeds.onCustomize`): the
 *   dialog for the embed's sheet, the repoint offered; when it comes back with `repoint`, the embed
 *   is pointed at the copy's sheet with 1.AB-1's `repointEmbed` (one undo step, saved as any edit;
 *   `repoint.ts`, loaded by `import()` so the editor stays out of the entry's static graph),
 *   then the copy's form opens (`/sheets/<id>`, the page's leave flushes the repoint first); a card
 *   embed whose repoint was applied stays on the page instead (2.M deliverable 22, DECISIONS 800.2).
 *
 * The dialog itself (fields, `variants.create`, refusals, Cancel) is 1.AC-2's: Cancel resolves null
 * and nothing happens here. The flags are read by the callers: `ComposeProviders` passes
 * `fromReference` only with `reference` and `statblock_form` on, and `buildPartOptions` passes
 * `fromEmbed` on the same rule.
 */
import type { CodexEditorHandle } from '@codex/editor';
import type { EmbedTarget } from '@codex/editor/embeds';
import { useNavigate } from '@tanstack/react-router';
import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { CustomizeTarget } from '../reference/actions.js';
import { sheetFormHref } from '../sheet-form/href.js';
import {
  CustomizeDialogProvider,
  type CustomizeRequest,
  type CustomizeResult,
  useCustomizeDialog,
} from '../sheet-form/variant/CustomizeDialog.js';

export type Editor = CodexEditorHandle['editor'];

/** What 1.AB-1's embed view hands its `onCustomize`. */
export interface EmbedCustomizeRequest {
  readonly blockId: string;
  readonly target: EmbedTarget;
  readonly documentId: string;
}

export interface CustomizeHandlers {
  readonly fromReference: (target: CustomizeTarget) => void;
  readonly fromEmbed: (embed: EmbedCustomizeRequest, editor: Editor | null) => void;
}

/**
 * The dialog request for an embed, the repoint offered: a stat block by its sheet, a card by its
 * page (2.M deliverable 22: the card branch repoints a card embed too).
 */
export function embedRequest(embed: EmbedCustomizeRequest): CustomizeRequest {
  return embed.target.kind === 'statblock'
    ? { sourceSheetId: embed.target.sheetId, offerRepoint: true }
    : { sourceDocId: embed.target.docId, title: '', offerRepoint: true };
}

const HandlersContext = createContext<CustomizeHandlers | null>(null);

/** The handlers under a `CustomizeProvider`; null outside one (no "Customize" is offered). */
export function useCustomizeHandlers(): CustomizeHandlers | null {
  return useContext(HandlersContext);
}

function Handlers({ children }: { readonly children?: ReactNode }) {
  const { open } = useCustomizeDialog();
  const navigate = useNavigate();
  const handlers = useMemo<CustomizeHandlers>(() => {
    const toForm = (result: CustomizeResult) =>
      void navigate({ href: sheetFormHref(result.documentId) });
    return {
      fromReference: (target) => {
        void open({ sourceDocId: target.docId, title: target.title, offerRepoint: false }).then(
          (result) => {
            if (result) toForm(result);
          },
        );
      },
      fromEmbed: (embed, editor) => {
        void open(embedRequest(embed)).then((result) => {
          if (!result) return;
          // The repoint is loaded on demand (`repoint.ts`): the editor stays out of the entry. The
          // copy exists either way, so its form opens even when the chunk or the repoint fails. A
          // card repoint that was applied ends on the page (2.M deliverable 22, DECISIONS 800.2):
          // one Mod-Z there points the embed back.
          void import('./repoint.js')
            .then(({ applyRepoint }) => applyRepoint(result, embed, editor))
            .catch(() => false)
            .then((applied) => {
              if (applied && embed.target.kind === 'card') return;
              toForm(result);
            });
        });
      },
    };
  }, [open, navigate]);
  return <HandlersContext.Provider value={handlers}>{children}</HandlersContext.Provider>;
}

/** 1.AC-2's dialog and the handlers over it. */
export function CustomizeProvider({ children }: { readonly children?: ReactNode }) {
  return (
    <CustomizeDialogProvider>
      <Handlers>{children}</Handlers>
    </CustomizeDialogProvider>
  );
}
